import React from "react";
import { cssForPDF } from "../../utilities/exportCSS";
import { dateParse } from './utils';

/**
 * PDFReportTemplate renders a static XHTML document for PDF generation.
 *
 * Props contract:
 *   data.guidanceHtml  — pre-processed HTML from buildGuidanceHtml() in index.js.
 *                        Raw markdown must NOT be passed; the JAR does not parse it.
 *   data.notesHtml     — pre-processed HTML from buildNotesHtml() in index.js.
 *                        Backtick spans are converted to <p class="code">, bare
 *                        angle brackets are entity-encoded, \n becomes <br/>.
 *
 * `testedURLsLinkList` order determines page numbers; URL cells are rendered in that
 * same stable order so page numbers always match the link list at the top.
 */
export default function PDFReportTemplate(props) {
  const { title, headerData, params, showTable, testedURLsLinkList, newData } = props;

  return (
    <html xmlns="http://www.w3.org/1999/xhtml">
      <head>
        <title>{title}</title>
        <meta name="subject" content={title} />
        <meta name="description" content="Accessibility Indicators Data Test done by Publicis Sapient to do a temperature check of the Accessibility of the website in question" />
        <meta name="author" content="Publicis Sapient" />
        <style dangerouslySetInnerHTML={{ __html: cssForPDF.data }} />
      </head>
      <body>
        <div id="topReportInfo">
          <h1 className="page-header">{`${headerData.company} Accessibility Report`}</h1>
          <p id="scoreh2">{`Score: ${(params.score || headerData.score).toFixed(0)}%`}</p>
          <p id="dateLbl">{`Test Completion Date: ${dateParse(headerData.date).replaceAll(".", "")}`}</p>
          <p>{`Domains Tested: ${headerData.urlTested}`}</p>
          <div id="logo">
            <img src="../PS_Logo.png" alt="Publicis Sapient" />
          </div>
        </div>

        {showTable && (
          <div className="link-list">
            <h2>List of Links Tested</h2>
            <p>This list contains the pages that were tested and used in the report below.</p>
            <ul>
              {testedURLsLinkList.map((link, x) => (
                <li key={`link-list-${x}`}>
                  {link.title}: <a href={link.url}>{link.url}</a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="sc-dhKdcB cVsNLU print-border">
          <div id="table">
            <div className="Section1">
              {newData.length > 0 && newData.map((data, idx) => {
                // Resolve the tested links for this row, ordered by their stable
                // position in testedURLsLinkList so page numbers are consistent.
                const rowLinks = testedURLsLinkList.filter(
                  link => link.criteriaId.includes(data.criteriaid)
                );

                return (
                  <React.Fragment key={`criteria-${idx}`}>
                    <figcaption id={`criteria-table-caption-${idx}`}>
                      <h2>
                        <p>
                          <b>{data.criteria}</b>
                        </p>
                      </h2>
                    </figcaption>
                    <table id="rtable" aria-label={`criteria-table-caption-${idx}`}>
                      <thead id="reportHead">
                        <tr>
                          <th id="guidanceCol">Guidance</th>
                          <th id="urlCol">URLs Tested</th>
                          <th id="noteCol">Notes</th>
                          <th id="capabilityCol">Capability</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {/*
                            guidanceHtml is pre-processed HTML (markdown parsed + symbols
                            replaced + <code> swapped for <p class="code">) by the caller.
                          */}
                          <td
                            className="reportCells guidance"
                            dangerouslySetInnerHTML={{ __html: data.guidanceHtml }}
                          />
                          <td className="reportCells" id="urlRow">
                            <ul>
                              {rowLinks.length > 0
                                ? rowLinks.map((link) => (
                                    <li key={link.url}>
                                      <a
                                        className="links-markdown"
                                        href={link.url}
                                        target="_blank"
                                      >
                                        {link.title}
                                      </a>
                                    </li>
                                  ))
                                : /* Fallback: row URL field doesn't contain recognisable http links.
                                     Mirror the old code: render the raw url string as plain text. */
                                  <li>{data.url.replaceAll("\\n", "\n")}</li>
                              }
                            </ul>
                          </td>
                          <td
                            className="reportCells"
                            dangerouslySetInnerHTML={{ __html: data.notesHtml }}
                          />
                          <td className="reportCells" id="capabilityCol">
                            {data.capability}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}